import { lookupsApi } from '../api/modules/lookups.api';
import { getApiErrorMessage } from '../api/client';
import { USE_API } from '../config/api';
import { villageMasterSeed } from '../data/master/villages';
import { businessUnitSeed, siteSeed } from '../data/master/businessUnits';
import { equipmentMasterSeed } from '../data/master/equipments';

/** Map lookup row → shape expected by VillageSelect / findVillageById. */
export function villageOptionFromLookup(row) {
  if (!row) return null;
  return {
    id: row.id,
    villageId: row.code ?? '',
    villageName: row.name ?? '',
    talukaName: row.talukaName ?? '',
    pin: row.pin ?? '',
    district: '',
    state: '',
    country: '',
    gramPanchayat: '',
    category: '',
    active: true,
  };
}

export function equipmentTypeOptionFromLookup(row) {
  if (!row) return null;
  return {
    id: row.id,
    equipmentId: row.code ?? '',
    name: row.name ?? '',
    code: row.code ?? '',
    active: true,
  };
}

export function businessUnitOptionFromLookup(row) {
  if (!row) return null;
  return {
    id: row.id,
    buId: row.code ?? '',
    shortName: row.name ?? '',
    code: row.code ?? '',
    active: true,
  };
}

export function siteOptionFromLookup(row) {
  if (!row) return null;
  return {
    id: row.id,
    siteId: row.code ?? '',
    siteName: row.name ?? '',
    buDbId: row.buId ?? null,
    buId: row.buCode ?? '',
    code: row.code ?? '',
    active: true,
  };
}

export function vendorOptionFromLookup(row) {
  if (!row) return null;
  return {
    id: row.id,
    vendorId: row.code ?? '',
    tradeAgencyName: row.name ?? '',
    code: row.code ?? '',
    active: true,
  };
}

let villagesLookupCache = null;
let villagesLookupInflight = null;

export function clearVillagesLookupCache() {
  villagesLookupCache = null;
  villagesLookupInflight = null;
}

export async function lookupVillages({ force = false } = {}) {
  if (!USE_API) {
    return villageMasterSeed.map((v) => ({
      id: v.id ?? v.villageId,
      villageId: v.villageId,
      villageName: v.villageName,
      talukaName: v.talukaName,
      pin: v.pin,
      active: true,
    }));
  }
  if (!force && villagesLookupCache) return villagesLookupCache;
  if (!force && villagesLookupInflight) return villagesLookupInflight;

  villagesLookupInflight = (async () => {
    try {
      const rows = await lookupsApi.villages();
      villagesLookupCache = (Array.isArray(rows) ? rows : []).map(villageOptionFromLookup);
      return villagesLookupCache;
    } catch (error) {
      throw new Error(getApiErrorMessage(error, 'Failed to load village options'));
    } finally {
      villagesLookupInflight = null;
    }
  })();

  return villagesLookupInflight;
}

export async function lookupEquipmentTypes() {
  if (!USE_API) {
    return equipmentMasterSeed.map((e, i) => ({
      id: e.id ?? i + 1,
      equipmentId: e.equipmentId,
      name: e.name,
      code: e.equipmentId,
      active: true,
    }));
  }
  try {
    const rows = await lookupsApi.equipmentTypes();
    return (Array.isArray(rows) ? rows : []).map(equipmentTypeOptionFromLookup);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load equipment type options'));
  }
}

export async function lookupBusinessUnits() {
  if (!USE_API) {
    return businessUnitSeed.map((b) => ({
      id: b.id,
      buId: b.buId,
      shortName: b.shortName,
      active: true,
    }));
  }
  try {
    const rows = await lookupsApi.businessUnits();
    return (Array.isArray(rows) ? rows : []).map(businessUnitOptionFromLookup);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load business unit options'));
  }
}

export async function lookupSites(params = {}) {
  if (!USE_API) {
    let items = [...siteSeed];
    if (params.buId) {
      items = items.filter((s) => s.buDbId === params.buId || s.buId === params.buId);
    }
    return items.map((s) => ({
      id: s.id,
      siteId: s.siteId,
      siteName: s.siteName,
      buDbId: s.buDbId,
      buId: s.buId,
      active: true,
    }));
  }
  try {
    const rows = await lookupsApi.sites(params);
    return (Array.isArray(rows) ? rows : []).map(siteOptionFromLookup);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load site options'));
  }
}

export async function lookupVendors() {
  if (!USE_API) return [];
  try {
    const rows = await lookupsApi.vendors();
    return (Array.isArray(rows) ? rows : []).map(vendorOptionFromLookup);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load vendor options'));
  }
}
